import React, { useEffect } from "react";
import { AlertCircle, CheckCircle2, X } from "lucide-react";

/** Replaces alert(): non-blocking, dismissible, and readable by screen readers. */
export function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(onClose, toast.type === "error" ? 9000 : 5000);
    return () => window.clearTimeout(t);
  }, [toast, onClose]);
  if (!toast) return null;
  const ok = toast.type === "success";
  return (
    <div
      role={ok ? "status" : "alert"}
      className={`fixed right-4 top-20 z-[70] flex max-w-sm items-start gap-3 rounded-xl border px-4 py-3 text-[13px] shadow-lg ${
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"
      }`}
    >
      {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />}
      <span className="whitespace-pre-wrap">{toast.msg}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" className="shrink-0 opacity-60 hover:opacity-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Replaces window.confirm(). */
export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", tone = "primary", busy, onCancel, onConfirm }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && !busy && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);
  if (!open) return null;
  const confirmCls = tone === "danger" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#1565c0] hover:bg-[#0f4f99]";
  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="absolute inset-0 bg-slate-900/40" aria-label="Cancel" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <h3 className="m-0 text-[16px] font-bold text-slate-900">{title}</h3>
        <p className="mb-0 mt-2 text-[13px] leading-relaxed text-slate-600">{body}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" disabled={busy} onClick={onCancel} className="rounded-xl border border-slate-300 px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="button" disabled={busy} onClick={onConfirm} className={`rounded-xl px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-60 ${confirmCls}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
